import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Copy, Mail, MessageSquareText, QrCode, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { cn } from '@/shared/lib/cn';
import { copyText, mailtoUrl, nativeShare, smsUrl, whatsappUrl, type ShareChannels } from '../share';
import { ReferralQr } from './referral-qr';

interface Props {
  url: string;
  code: string;
  shareText: string;
  brandName: string;
  /** Canais que funcionam onde a página está (ver `shareChannels`). */
  channels: ShareChannels;
  inApp: boolean;
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.8h-.01a9.8 9.8 0 0 1-5-1.37l-.36-.21-3.72.97 1-3.62-.24-.37a9.8 9.8 0 1 1 8.33 4.6m8.34-18.14A11.7 11.7 0 0 0 12.05.2C5.54.2.25 5.5.24 12a11.7 11.7 0 0 0 1.57 5.86L.14 24l6.29-1.65a11.8 11.8 0 0 0 5.61 1.43h.01c6.5 0 11.8-5.3 11.8-11.8 0-3.15-1.23-6.11-3.46-8.34" />
    </svg>
  );
}

/** Link exclusivo + todos os jeitos de mandar: é o centro da página. */
export function ShareCard({ url, code, shareText, brandName, channels, inApp }: Props) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const display = url.replace(/^https?:\/\//, '');
  // Botão principal: WhatsApp quando abre; senão a folha nativa; senão copiar.
  const primary = channels.whatsapp ? 'whatsapp' : channels.native ? 'native' : 'copy';
  const secondary = [
    channels.native && primary !== 'native' ? 'native' : null,
    channels.sms ? 'sms' : null,
    channels.email ? 'email' : null,
    'qr',
  ].filter((x): x is 'native' | 'sms' | 'email' | 'qr' => x !== null);

  const copy = async () => {
    const ok = await copyText(url);
    if (ok) {
      setCopied(true);
      toast.success('Link copiado! Agora é só colar para os amigos.');
      window.setTimeout(() => setCopied(false), 2200);
    } else {
      toast.error('Não deu para copiar. Toque e segure no link para copiar.');
    }
  };

  const shareNative = async () => {
    const result = await nativeShare(window, { title: `Convite ${brandName}`, text: shareText, url });
    if (result === 'unavailable') void copy();
  };

  const go = (href: string) => {
    window.location.href = href;
  };

  return (
    <Card className="p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Seu link exclusivo</p>
      <div className="mt-3 flex items-center gap-2 rounded-xl border border-border/70 bg-foreground/[0.03] p-1.5 pl-3">
        <p className="min-w-0 flex-1 select-all truncate font-mono text-sm" title={url}>
          {display}
        </p>
        <Button size="sm" variant="secondary" onClick={() => void copy()} aria-label="Copiar link" className="shrink-0">
          <AnimatePresence mode="wait" initial={false}>
            {copied ? (
              <motion.span key="ok" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex items-center gap-1">
                <Check className="size-4 text-success" /> Copiado
              </motion.span>
            ) : (
              <motion.span key="copy" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex items-center gap-1">
                <Copy className="size-4" /> Copiar
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Código <span className="mgm-accent-text font-mono font-semibold tracking-widest">{code}</span>
      </p>

      {primary === 'whatsapp' ? (
        <motion.button
          type="button"
          whileTap={{ scale: 0.98 }}
          onClick={() => go(whatsappUrl(shareText, inApp))}
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] text-base font-semibold text-white shadow-soft transition-colors hover:bg-[#1fbe5b]"
        >
          <WhatsAppIcon className="size-5" /> Enviar no WhatsApp
        </motion.button>
      ) : (
        <motion.button
          type="button"
          whileTap={{ scale: 0.98 }}
          onClick={() => void (primary === 'native' ? shareNative() : copy())}
          className="mgm-accent-bg mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold shadow-soft"
        >
          {primary === 'native' ? (
            <>
              <Share2 className="size-5" /> Compartilhar com amigos
            </>
          ) : (
            <>
              <Copy className="size-5" /> Copiar link para enviar
            </>
          )}
        </motion.button>
      )}
      {primary === 'copy' ? (
        <p className="mt-2 text-center text-xs text-muted-foreground">Depois é só colar no WhatsApp, Instagram ou onde preferir.</p>
      ) : null}

      <div
        className={cn(
          'mt-2 grid gap-2',
          secondary.length >= 4 ? 'grid-cols-4' : secondary.length === 3 ? 'grid-cols-3' : secondary.length === 2 ? 'grid-cols-2' : 'grid-cols-1',
        )}
      >
        {secondary.includes('native') ? (
          <Button variant="secondary" className="h-auto flex-col gap-1 py-2.5 text-xs" onClick={() => void shareNative()}>
            <Share2 className="size-4" /> Mais
          </Button>
        ) : null}
        {secondary.includes('sms') ? (
          <Button variant="secondary" className="h-auto flex-col gap-1 py-2.5 text-xs" onClick={() => go(smsUrl(shareText))}>
            <MessageSquareText className="size-4" /> SMS
          </Button>
        ) : null}
        {secondary.includes('email') ? (
          <Button
            variant="secondary"
            className="h-auto flex-col gap-1 py-2.5 text-xs"
            onClick={() => go(mailtoUrl(`Convite ${brandName}`, shareText))}
          >
            <Mail className="size-4" /> E-mail
          </Button>
        ) : null}
        <Button
          variant="secondary"
          className={cn('h-auto flex-col gap-1 py-2.5 text-xs', showQr && 'mgm-accent-soft', secondary.length === 1 && 'flex-row')}
          onClick={() => setShowQr((v) => !v)}
          aria-expanded={showQr}
        >
          <QrCode className="size-4" /> {secondary.length === 1 ? 'Mostrar QR Code' : 'QR Code'}
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {showQr ? (
          <motion.div
            key="qr"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4">
              <ReferralQr url={url} />
              <p className="mt-2 text-center text-xs text-muted-foreground">Mostre para o seu amigo apontar a câmera.</p>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Card>
  );
}
